










































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { TypeformForm } from '@/api/model/typeform/typeform-form'
import { TypeformResponseAnswerHook, TypeformResponseAnswerHookActionType } from '@/api/model/typeform/typeform-response-answer-hook'
import { generateReferralTypeOptions, InputSelectOption } from '@/helpers/options'
import { Branch } from '@/api/model/branch'
import { Pipeline } from '@/api/model/pipeline'
import { PipelineAPI, TypeformResponseAnswerHookAPI } from '@/api/backend-client'
import HookPopOutCondition from '@/views/Company/Settings/Typeform/Forms/Detail/HookPopOutCondition.vue'
import IconCross from '@/components/icons/IconCross.vue'
import InputSelect from '@/components/input/InputSelect.vue'
import KvButton from '@/components/input/KvButton.vue'
import { v4 as uuid } from 'uuid'
import { TypeformFieldUnion } from '@/api/model/typeform/typeform-field'

@Component({
    computed: {
        TypeformResponseAnswerHookActionType() {
            return TypeformResponseAnswerHookActionType
        },
    },
    components: {
        KvButton,
        InputSelect,
        IconCross,
        HookPopOutCondition,
    },
})
export default class HookPopOut extends Vue {
    @Prop({ required: true }) readonly form!: TypeformForm
    @Prop({ required: true }) readonly field!: TypeformFieldUnion
    @Prop({ required: true }) readonly hooks!: TypeformResponseAnswerHook[]
    @Prop({ required: true, default: false }) readonly show!: boolean
    showPopOut: boolean = false
    pipelines: Pipeline[] = []
    loading: boolean = false

    mounted() {
        this.loadContent()
    }

    async loadContent(): Promise<void> {
        this.loading = true
        this.pipelines = await PipelineAPI.getOwnPipelines()
        this.loading = false
    }

    onConditionChanged(hookIndex: number, value: { index: number, value: string|number|boolean}) {
        this.hooks[hookIndex].answers[value.index] = value.value
    }

    onAddCondition(hookIndex: number) {
        this.hooks[hookIndex].answers.push(null)
    }

    onDeleteCondition(hookIndex: number, index: number): void {
        this.hooks[hookIndex].answers.splice(index, 1)

        if (this.hooks[hookIndex].answers.length === 0) {
            TypeformResponseAnswerHookAPI.delete(this.hooks[hookIndex].id)
            this.hooks.splice(hookIndex, 1)
        }
    }

    @Watch('show')
    onShowChanged() {
        this.showPopOut = this.show
    }

    @Watch('showPopOut')
    onShowPopOutChanged() {
        if (!this.showPopOut) {
            this.$emit('closed')
        }
    }

    addHook() {
        this.hooks.push(new TypeformResponseAnswerHook(
            uuid(),
            this.form.id,
            this.field.id,
            TypeformResponseAnswerHookActionType.CREATE_REFERRAL,
            [null],
            [this.createDefaultOptions()],
        ))
    }

    async saveHooks(callback: () => void): Promise<void> {
        this.$emit('save', this.hooks)
        callback()
    }

    async deleteHooks(callback: () => void): Promise<void> {
        this.$emit('delete', this.hooks)
        callback()
    }

    get availableActionTypes(): Array<{ label: string, value: string }> {
        return [
            {
                label: this.$t('company_settings.typeform_forms.hook.actionType.create-referral').toString(),
                value: TypeformResponseAnswerHookActionType.CREATE_REFERRAL,
            },
            {
                label: this.$t('company_settings.typeform_forms.hook.actionType.send-email').toString(),
                value: TypeformResponseAnswerHookActionType.SEND_EMAIL,
            },
        ]
    }

    getActionType(hook: TypeformResponseAnswerHook) {
        return hook.actionType
    }

    setActionType(hook: TypeformResponseAnswerHook, value: TypeformResponseAnswerHookActionType) {
        if (hook.actionType === value) {
            return
        }

        hook.actionType = value

        // Reset options
        hook.options.splice(0, hook.options.length)
        this.addOptionsEntry(hook)
    }

    get availableReferralTypes(): InputSelectOption[] {
        return generateReferralTypeOptions()
    }

    get availablePipelineOptions(): InputSelectOption[] {
        return this.pipelines.map(pipeline => ({
            label: pipeline.name,
            value: pipeline.id,
        }))
    }

    getPipelineValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].pipeline
    }

    setPipelineValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        if (hook.options[index].pipeline === value) {
            return
        }

        this.$set(hook.options[index], 'phase', null)
        this.$set(hook.options[index], 'pipeline', value)
    }

    getPhaseValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].phase
    }

    setPhaseValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'phase', value)
    }

    getReferralTypeValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].referralType
    }

    setReferralTypeValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'referralType', value)
    }

    getAssignedToBranchTypeValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].assignedToBranchType ?? this.createDefaultOptions().assignedToBranchType
    }

    setAssignedToBranchTypeValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'assignedToBranchType', value)
    }

    getAvailableAssignedToBranches(hook: TypeformResponseAnswerHook, index: number): Branch[] {
        const pipelineId = this.getPipelineValue(hook, index)
        if (!pipelineId) {
            return []
        }

        const pipeline = this.pipelines.find(p => p.id === pipelineId)
        if (!pipeline) {
            return []
        }

        return [...pipeline.branches].sort((a, b) => a.name.localeCompare(b.name))
    }

    getAssignedToBranchValue(hook: TypeformResponseAnswerHook, index: number) {
        const branchId = hook.options[index].assignedToBranch
        if (!branchId) {
            return null
        }

        const branch = this.getAvailableAssignedToBranches(hook, index).find(b => b.id === branchId)
        if (!branch) {
            return null
        }

        return branch
    }

    setAssignedToBranchValue(hook: TypeformResponseAnswerHook, index: number, value: Branch) {
        this.$set(hook.options[index], 'assignedToBranch', value ? value.id : null)
    }

    getEmailRecipientTypeValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].emailRecipientType ?? this.createDefaultOptions().emailRecipientType
    }

    setEmailRecipientTypeValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'emailRecipientType', value)
    }

    getEmailRecipientValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].emailRecipient
    }

    setEmailRecipientValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'emailRecipient', value)
    }

    getEmailBodyValue(hook: TypeformResponseAnswerHook, index: number) {
        return hook.options[index].emailBody
    }

    setEmailBodyValue(hook: TypeformResponseAnswerHook, index: number, value: string) {
        this.$set(hook.options[index], 'emailBody', value)
    }

    getPhasesForPipeline(hook: TypeformResponseAnswerHook, index: number) {
        if (!hook.options[index].pipeline) {
            return []
        }

        return this.pipelines.find(pipeline => pipeline.id === hook.options[index].pipeline).activePhases.map(phase => ({
            label: phase.name,
            value: phase.id,
        }))
    }

    createDefaultOptions() {
        return {
            pipeline: null,
            phase: null,
            referralType: null,
            assignedToBranchType: 'operating-zone',
            assignedToBranch: null,
            emailRecipientType: 'employee',
            emailRecipient: null,
            emailBody: null,
        }
    }

    addOptionsEntry(hook: TypeformResponseAnswerHook) {
        hook.options.push(this.createDefaultOptions())
    }

    removeOptionsEntry(hook: TypeformResponseAnswerHook, index: number) {
        if (hook.options.length <= 1) {
            return
        }

        hook.options.splice(index, 1)
    }

    showAddOptionsEntryButton(hook: TypeformResponseAnswerHook): boolean {
        if (hook.actionType !== TypeformResponseAnswerHookActionType.CREATE_REFERRAL) {
            return false
        }

        return hook.options.length >= 1
    }

    showRemoveOptionsEntryButton(hook: TypeformResponseAnswerHook): boolean {
        if (hook.actionType !== TypeformResponseAnswerHookActionType.CREATE_REFERRAL) {
            return false
        }

        return hook.options.length > 1
    }
}
